import React, { useEffect } from 'react';
import { Table, Row, Col, FormGroup, Label, Button } from 'reactstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useRowSelect,
} from 'react-table';

import Select from 'react-select';
import { Icon } from '@assets';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { unitTypes } from '@sparklib';

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input
          type='checkbox'
          className='custom-checkbox'
          ref={resolvedRef}
          {...rest}
        />
      </>
    );
  }
);

function GlobalFilter({
  preGlobalFilteredRows: _preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);

  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div className='search-bar d-inline-block inline-block w-100'>
      <FormGroup className='mb-0 w-auto d-inline-block'>
        <div className='d-flex align-items-center w-100'>
          <Label
            for='inquiry-search'
            className='mb-0 w-auto d-inline-flex align-items-center mr-2'
          >
            <Icon icon='search' />
          </Label>
          <input
            id='inquiry-search'
            value={value || ''}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
            placeholder='Search by keywords'
            className='w-100'
          />
        </div>
      </FormGroup>
    </div>
  );
}

const mobileStyles = {
  filterContainer: {
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '16px',
    background: '#f8fafc',
    boxShadow: '0 6px 18px rgba(15, 34, 58, 0.08)',
  },
  filterHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#475569',
  },
  filterLabel: {
    fontWeight: 600,
  },
  mobileActionsWrapper: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: '1 1 160px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 14px',
    borderRadius: '12px',
    boxShadow: '0 6px 16px rgba(15, 34, 58, 0.12)',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingBottom: '72px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 10px 28px rgba(15, 34, 58, 0.1)',
    border: '1px solid #eef2f7',
    cursor: 'pointer',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 auto',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  primaryName: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2933',
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
  },
  timeAgo: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  detailSection: {
    marginTop: '12px',
    display: 'grid',
    rowGap: '8px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '13px',
    color: '#334155',
    lineHeight: 1.45,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '16px',
  },
  actionPrimary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
  actionSecondary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationRow: {
    justifyContent: 'center',
    width: '100%',
  },
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const diff = moment(dateString);
  if (!diff.isValid()) return '';
  return diff.fromNow();
};

export default function ClientInquiryTable({ columns, data, rightAddons=null, leftAddons=null, changeIsChecked, setSidebarState, onConvert, onDelete, onView }) {
  var totalRecord = data.length;
  const pageSizeOptions = [
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '75', label: '75' },
    { value: '100', label: '100' },
    { value: totalRecord.toString(), label: 'All' },
  ];
  const defaultColumn = React.useMemo(
    () => ({
      width: 'auto',
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    rows,
    state: { pageIndex, globalFilter, sortBy },
    prepareRow,
  } = useTable(
    { columns, data, defaultColumn, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) =>
        changeIsChecked
          ? [
              // Let's make a column for selection
              {
                id: 'selection',
                Header: ({ getToggleAllPageRowsSelectedProps }) => (
                  <IndeterminateCheckbox
                    {...getToggleAllPageRowsSelectedProps()}
                  />
                ),
                Cell: ({ row }) => (
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} onClick={(e) => e.stopPropagation()}/>
                ),
                width: 50,
                maxWidth: 50,
              },
              ...columns,
            ]
          : columns
      );
    }
  );

  const selectedRowSignature = React.useMemo(
    () =>
      selectedFlatRows
        .map((row) => row.id || row.original?.node?.id || '')
        .join('|'),
    [selectedFlatRows]
  );

  useEffect(() => {
    if (changeIsChecked) {
      changeIsChecked(selectedFlatRows);
    }
  }, [selectedRowSignature, changeIsChecked]);

	const history = useHistory();
  const listCountLabel = `${rows.length} Lead${rows.length === 1 ? '' : 's'}`;
  const sortColumnLabel = React.useMemo(() => {
    if (!sortBy || sortBy.length === 0) {
      return 'Default Order';
    }
    const flatHeaders = headerGroups.flatMap((group) => group.headers);
    const match = flatHeaders.find((col) => col.id === sortBy[0].id);
    if (!match) return 'Custom';
    const headerValue = match.Header;
    if (typeof headerValue === 'string') {
      return headerValue;
    }
    if (typeof headerValue === 'function') {
      return 'Custom';
    }
    if (React.isValidElement(headerValue)) {
      return 'Custom';
    }
    return `${match.id}`.replace(/_/g, ' ');
  }, [sortBy, headerGroups]);

  const mobileRightAddons = rightAddons
    ? React.Children.toArray(rightAddons)
        .map((child, index) => {
          if (!React.isValidElement(child)) return child;
          const existingClasses = child.props.className || '';
        const cleanedClasses = existingClasses
          .split(' ')
          .filter(
            (cls) =>
              cls !== 'float-right' && cls !== 'mr-2' && cls !== 'mt-sm-0'
          )
          .join(' ')
          .trim();

        return React.cloneElement(child, {
          key: index,
          className: `${cleanedClasses} mobile-action-btn`.trim(),
          style: { ...mobileStyles.actionButton, ...(child.props.style || {}) },
        });
        })
        .filter(Boolean)
    : null;

  const handleRowView = (id) => {
    if (!id) {
      return;
    }
    if (setSidebarState) {
      setSidebarState(true);
    }
    if (onView) {
      onView(id);
    }
    history.push(`/client-inquiries/${id}`);
  };

  return (
    <>
      <div className='d-none d-sm-block'>
        <Row className='my-2 d-flex align-items-start'>
          <Col sm='5'>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
            {leftAddons}
          </Col>

          <Col sm='7' className='text-lg-left'>
            {rightAddons}
          </Col>
        </Row>

        <Table
          responsive
          className='client-list text-left min-w-900'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps([
                      {
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      },
                      column.getSortByToggleProps(),
                    ])}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <>
                            &nbsp; <Icon icon='sort-down' size='lg' />
                          </>
                        ) : (
                          <>
                            &nbsp; <Icon icon='sort-up' size='lg' />
                          </>
                        )
                      ) : column.canSort ? (
                        <>
                          &nbsp; <Icon icon='sort' size='lg' />
                        </>
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => handleRowView(row.original.node.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {row.cells.map((cell) => {
                    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                  })}
                </tr>
              );
            })}
            {page.length <= 2 ? (
              <tr>
                <div className='spacer-div'></div>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      <div className='d-block d-sm-none'>
        <div style={mobileStyles.filterContainer}>
          <div style={mobileStyles.filterHeader}>
            <div onClick={(e) => e.stopPropagation()}>
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </div>
            {leftAddons ? (
              <div onClick={(e) => e.stopPropagation()}>{leftAddons}</div>
            ) : null}
            <div style={mobileStyles.filterRow}>
              <span style={mobileStyles.filterLabel}>{listCountLabel}</span>
              <span>Sort By: {sortColumnLabel}</span>
            </div>
          </div>
          <div style={mobileStyles.mobileActionsWrapper}>{mobileRightAddons}</div>
        </div>

        <div style={mobileStyles.listWrapper}>
          {page.map((row) => {
            prepareRow(row);
            const node = row.original?.node || {};
            const selectionProps = row.getToggleRowSelectedProps();
            const nodeIdValue = node.id ?? '';
            const nodeIdNumber = node.id ? Number(node.id) : null;
            const timeAgo =
              formatRelativeTime(node.updatedAt) ||
              formatRelativeTime(node.createdAt);
            const cityName = node.city?.name || 'City not set';
            const leaseTerm = node.leaseTerm
              ? `${node.leaseTerm} month lease`
              : null;
            let budgetLabel = '-';
            if (node.minRent && node.maxRent) {
              budgetLabel = `$${Number(node.minRent).toLocaleString()} - $${Number(
                node.maxRent
              ).toLocaleString()}`;
            } else if (node.minRent) {
              budgetLabel = `$${Number(node.minRent).toLocaleString()} & Above`;
            } else if (node.maxRent) {
              budgetLabel = `Below $${Number(node.maxRent).toLocaleString()}`;
            }

            const availFrom = node.availFrom;
            const availTo = node.availTo;
            let moveInLabel = '-';
            if (availFrom && !availTo) {
              moveInLabel = `After ${moment(availFrom).format('L')}`;
            } else if (!availFrom && availTo) {
              moveInLabel = `Before ${moment(availTo).format('L')}`;
            } else if (availFrom && availTo) {
              moveInLabel = `${moment(availFrom).format('L')} - ${moment(
                availTo
              ).format('L')}`;
            }

            const bedLabel = Array.isArray(node.bedroomArray) && node.bedroomArray.length
              ? node.bedroomArray
                  .map((bed) => unitTypes?.[bed] || bed)
                  .join(', ')
              : '-';
            const bathLabel =
              node.bathroom !== undefined && node.bathroom !== null
                ? `${node.bathroom}+ Bath`
                : '-';

            return (
              <div
                key={row.id}
                style={mobileStyles.card}
                onClick={() => handleRowView(nodeIdValue)}
              >
                <div style={mobileStyles.headerRow}>
                  <div style={mobileStyles.headerLeft}>
                    <div style={mobileStyles.nameRow}>
                      <div
                        style={mobileStyles.checkboxWrapper}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IndeterminateCheckbox {...selectionProps} />
                      </div>
                      <span style={mobileStyles.primaryName}>
                        {node.name || 'Lead'}
                      </span>
                    </div>
                    <div style={mobileStyles.subtitle}>{cityName}</div>
                    {leaseTerm ? (
                      <div style={mobileStyles.subtitle}>{leaseTerm}</div>
                    ) : null}
                  </div>
                  <div>
                    <span style={mobileStyles.timeAgo}>{timeAgo}</span>
                  </div>
                </div>

                <div style={mobileStyles.detailSection}>
                  {node.email ? (
                    <div>
                      <div style={mobileStyles.detailLabel}>Email</div>
                      <div style={mobileStyles.detailValue}>
                        <a
                          href={`mailto:${node.email}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {node.email}
                        </a>
                      </div>
                    </div>
                  ) : null}
                  {node.phone ? (
                    <div>
                      <div style={mobileStyles.detailLabel}>Phone</div>
                      <div style={mobileStyles.detailValue}>
                        <a
                          href={`tel:${node.phone}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {node.phone}
                        </a>
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <div style={mobileStyles.detailLabel}>Budget</div>
                    <div style={mobileStyles.detailValue}>{budgetLabel}</div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>Move-In</div>
                    <div style={mobileStyles.detailValue}>{moveInLabel}</div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>Beds & Baths</div>
                    <div style={mobileStyles.detailValue}>
                      {bedLabel} / {bathLabel}
                    </div>
                  </div>
                </div>

                <div
                  style={mobileStyles.actionRow}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    color='primary'
                    style={mobileStyles.actionPrimary}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowView(nodeIdValue);
                    }}
                  >
                    View Details
                  </Button>
                  {onConvert && nodeIdNumber !== null ? (
                    <Button
                      color='success'
                      style={mobileStyles.actionSecondary}
                      onClick={(e) => {
                        e.stopPropagation();
                        onConvert(nodeIdNumber);
                      }}
                    >
                      Convert to Client
                    </Button>
                  ) : null}
                  {onDelete && nodeIdNumber !== null ? (
                    <Button
                      color='danger'
                      style={mobileStyles.actionSecondary}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(nodeIdNumber);
                      }}
                    >
                      Delete Lead
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Row className='d-flex align-items-center mt-4 flex-column flex-md-row'>
        <Col
          md='6'
          className='d-flex justify-content-md-start justify-content-center'
        >
          <FormGroup className='mb-3 mb-md-0'>
            <Label
              for='inquiry-page-size'
              className='mb-0 w-auto d-inline-block mr-2 mr-md-3'
            >
              Showing Entries
            </Label>
            <Select
              inputId='inquiry-page-size'
              name='PageSize'
              defaultValue={pageSizeOptions[0]}
              options={pageSizeOptions}
              onChange={(e) => {
                setPageSize(Number(e.value));
              }}
              className='basic-multi-select text-left'
              classNamePrefix='default-select'
              menuPlacement={'auto'}
            />
          </FormGroup>
        </Col>

        <Col
          md='6'
          className='d-flex justify-content-center mt-2 mt-md-0'
        >
          <ul className='pagination'>
            <li onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
                <Icon icon='chevron-left' />
              </a>
            </li>
            <li onClick={() => previousPage()} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
              </a>
            </li>
            <span className='d-flex align-items-center px-3'>
              Page &nbsp; <strong>{pageIndex + 1}</strong> &nbsp; of &nbsp;
              <strong>{pageOptions.length}</strong>
            </span>
            <li onClick={() => nextPage()} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
              </a>
            </li>
            <li onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
                <Icon icon='chevron-right' />
              </a>
            </li>
          </ul>
        </Col>
      </Row>
    </>
  );
}
